import { useRemoteSelect } from '@neo/hooks/use-remote-select'
import { RadioGroup, RadioGroupItem } from '@neo/primitives/base/radio-group'
import { Label } from '@neo/primitives/label'
import { Select } from '@neo/primitives/select'
import { useEffect, useState } from 'react'
import { DeletePopupDescription } from 'v2/shared/components/misc'
import { settingsService } from 'v2/shared/services/settings-service'
import { getAPIConsoleQueryKeys } from 'v2/shared/utils/query-key-management'
import type {
  RPlivoApplication,
  RPlivoApplicationInternal,
  RPlivoApplications,
} from 'v2/types/api/settings'

export interface DeleteApplicationChoice {
  cascade: boolean
  newEndpointApplication: string
  valid: boolean
}

interface DeleteApplicationOptionsProps {
  app: RPlivoApplicationInternal
  onChange: (choice: DeleteApplicationChoice) => void
}

const REASSIGN = 'reassign'
const DELETE = 'delete'

const pluralEndpoints = (count: number) => (count === 1 ? 'endpoint' : 'endpoints')

type RemapApplicationSelectProps = {
  value: string
  excludeAppId: string
  onChange: (value: string) => void
}

const RemapApplicationSelect = ({ value, excludeAppId, onChange }: RemapApplicationSelectProps) => {
  const { options, setSearchTerm, isLoading, loadMore } = useRemoteSelect({
    queryKey: [...getAPIConsoleQueryKeys().applications, 'remap', excludeAppId],
    enableOffsetMode: true,
    selectedValue: value,
    searchKey: 'app_name',
    fetchResource: (id) => settingsService.getApplication(id as string),
    optionFromResource(data) {
      const app = data as RPlivoApplication
      return { label: app.app_name, value: app.app_id }
    },
    fetchService: settingsService.listApplications,
    optionExtractor(data) {
      const applications = data as RPlivoApplications
      return {
        options: applications.objects
          .filter((app) => app.app_id !== excludeAppId)
          .map((app) => ({ label: app.app_name, value: app.app_id })),
        hasNextPage: !!applications.meta?.next,
      }
    },
  })

  return (
    <Select
      options={options}
      placeholder="Select application"
      className="w-full"
      value={value}
      onChange={(next) => onChange(Array.isArray(next) ? next[0] || '' : next)}
      loading={isLoading}
      searchable={true}
      onSearchChange={setSearchTerm}
      onLoadMore={loadMore}
    />
  )
}

export const DeleteApplicationOptions = ({ app, onChange }: DeleteApplicationOptionsProps) => {
  const endpointsCount = app.endpoints_count ?? 0
  const numbersCount = app.phone_numbers_count ?? 0
  const hasEndpoints = endpointsCount > 0

  const [mode, setMode] = useState<string>(REASSIGN)
  const [target, setTarget] = useState('')

  useEffect(() => {
    if (!hasEndpoints || mode === DELETE) {
      onChange({ cascade: true, newEndpointApplication: '', valid: true })
      return
    }
    onChange({ cascade: false, newEndpointApplication: target, valid: !!target })
  }, [hasEndpoints, mode, target, onChange])

  return (
    <div className="flex flex-col gap-4">
      <DeletePopupDescription
        name={app.app_name}
        resource="application"
        action="delete"
        subText={hasEndpoints ? undefined : 'You cannot undo this action.'}
      />

      {hasEndpoints && (
        <div className="flex flex-col gap-3">
          <div className="text-s-500 text-foreground">
            This application has {endpointsCount} linked {pluralEndpoints(endpointsCount)}. What
            should happen to them?
          </div>

          <RadioGroup
            name="endpoint-handling"
            value={mode}
            onValueChange={setMode}
            className="flex flex-col gap-3"
            data-testid="radiogroup-endpoint-handling"
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-start space-x-2">
                <RadioGroupItem
                  value={REASSIGN}
                  id="ep-reassign"
                  className="mt-1 flex-shrink-0"
                  data-testid="radio-reassign-endpoints"
                />
                <Label htmlFor="ep-reassign" className="text-p-500">
                  <div className="flex flex-col">
                    <span className="text-s-500 text-foreground">
                      Reassign to another application
                    </span>
                    <span className="text-s-400 text-tertiary">
                      The endpoints keep working — they move to the application you pick.
                    </span>
                  </div>
                </Label>
              </div>

              {mode === REASSIGN && (
                <div className="ml-6 flex flex-col gap-1" data-testid="combobox-remap-application">
                  <RemapApplicationSelect
                    value={target}
                    excludeAppId={app.app_id}
                    onChange={setTarget}
                  />
                  {!target && (
                    <span className="text-s-400 text-status-danger">
                      Pick an application to move the endpoints to.
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-start space-x-2">
              <RadioGroupItem
                value={DELETE}
                id="ep-delete"
                className="mt-1 flex-shrink-0"
                data-testid="radio-delete-endpoints"
              />
              <Label htmlFor="ep-delete" className="text-p-500">
                <div className="flex flex-col">
                  <span className="text-s-500 text-foreground">
                    Delete the endpoints permanently
                  </span>
                  <span className="text-s-400 text-tertiary">
                    The {endpointsCount} {pluralEndpoints(endpointsCount)} will stop working and
                    can't be recovered.
                  </span>
                </div>
              </Label>
            </div>
          </RadioGroup>

          {numbersCount > 0 && (
            <div className="text-s-400 text-tertiary bg-muted rounded-md p-3">
              {numbersCount} {numbersCount === 1 ? 'number' : 'numbers'} linked to this application
              will be unlinked and routed to your default application.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
